import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useFormContext } from "react-hook-form";

// The models each provider is priced for in voxi-2-0 (DECISION_RATES in src/pricing.js); the
// first is the provider's default. A model outside this list still runs, but its cost is omitted.
const MODELS: Record<string, string[]> = {
  jev: ["jev-latest"],
  openai: ["gpt-6-luna"],
};

export default function BargeInTab() {
  const { control, watch, setValue } = useFormContext();
  const enabled = watch("barge_in_enabled");
  const provider: string = watch("barge_in_provider") || "jev";
  const savedModel: string = watch("barge_in_model") || "";
  const known = MODELS[provider] ?? [];
  // A model saved earlier that is not in the list stays selectable, so opening and saving the
  // form does not change it.
  const models = savedModel && !known.includes(savedModel) ? [...known, savedModel] : known;

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium">Short interruptions</h4>
      <FormField
        control={control}
        name="barge_in_enabled"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center justify-between gap-4">
              <div>
                <FormLabel>Ask a model about short interruptions</FormLabel>
                <FormDescription>
                  When the caller says one or two words over the agent, a small model
                  decides whether they are just acknowledging ("yeah") or taking the
                  turn ("Hello?"). On by default; turn it off to use the word-count rule
                  for this assistant. When the model gives no answer in time the
                  word-count rule decides too. V2 calls only.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Toggle barge-in decisions"
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {enabled && (
        <>
          <FormField
            control={control}
            name="barge_in_provider"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Provider</FormLabel>
                <Select
                  value={field.value || "jev"}
                  onValueChange={(v) => {
                    field.onChange(v);
                    // A model belongs to one provider: go back to the new provider's default.
                    setValue("barge_in_model", MODELS[v]?.[0] ?? "", { shouldDirty: true });
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Jev" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="jev">Jev</SelectItem>
                    <SelectItem value="openai">OpenAI Decisions</SelectItem>
                  </SelectContent>
                </Select>
                <FormDescription>
                  Jev is the more accurate on our measurements; OpenAI answers faster.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="barge_in_model"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Model</FormLabel>
                <Select value={field.value || models[0] || ""} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Provider default" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="barge_in_threshold"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Threshold</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="0.99"
                    placeholder="Provider default"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The caller takes the turn when the model's probability of that is at
                  least this (between 0 and 1). Empty uses the provider's own default
                  (OpenAI 0.65, Jev its own pick).
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="barge_in_timeout_ms"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Wait for the model (ms)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="50"
                    min="100"
                    max="5000"
                    placeholder="600"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  How long to wait for an answer, 100 to 5000. The agent keeps talking
                  meanwhile. Empty is 600.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
}
